/**
 * Workflow v2 (state machine) types + validation — TypeScript mirror of
 * nodefork/src/utils/workflow/workflow-definition.util.ts. The server
 * re-validates on save; this copy powers live authoring feedback.
 */

export type StateKind = "start" | "task" | "decision" | "hold" | "terminal";
export type TransitionKind = "advance" | "hold" | "escalate" | "return";
export type CaptureType = "text" | "textarea" | "number" | "select" | "multiselect" | "boolean";
export type FieldOp = "notEmpty" | "equals" | "notEquals" | "gt" | "gte" | "lt" | "lte" | "in";

export interface ManualGuard { type: "manual"; roles?: string[] }
export interface FieldGuard { type: "field"; field: string; op: FieldOp; value?: string | number | boolean | Array<string | number> }
export interface AiReviewGuard { type: "aiReview"; prompt?: string }
export type WorkflowGuard = ManualGuard | FieldGuard | AiReviewGuard;

export interface WorkflowCaptureField {
  id: string;
  label: string;
  type: CaptureType;
  required?: boolean;
  options?: string[];
  placeholder?: string;
}

export interface WorkflowState {
  id: string;
  label: string;
  kind: StateKind;
  actor?: string;
  description?: string;
  capture?: WorkflowCaptureField[];
  ui?: "generic" | "pathway-session" | "medication-decision";
  /**
   * Requirement decision step this state implements (matches
   * clinicalSpec.decisionSequence[].step). The studio's Decision Sequence
   * block is derived from these tags, so the §8.3 traceability map follows
   * workflow edits instead of being maintained by hand.
   */
  decisionStep?: number;
}

export interface WorkflowTransition {
  id?: string;
  from: string;
  to: string;
  label: string;
  kind: TransitionKind;
  guard?: WorkflowGuard;
  /** §18.1 — the declared guardrail (clinicalSpec.guardrails.rules[].id) this
   *  transition's guard implements. Declared here, on the machinery, by its
   *  author; the §10.2 record DERIVES its Enforced-by display from these. */
  implementsGuardrail?: string;
}

export interface WorkflowV2 {
  version: 2;
  initial: string;
  states: WorkflowState[];
  transitions: WorkflowTransition[];
}

export type WorkflowValue = string[] | WorkflowV2 | undefined | null;

export const isExecutableWorkflow = (raw: unknown): raw is WorkflowV2 =>
  !!raw && typeof raw === "object" && !Array.isArray(raw) && (raw as WorkflowV2).version === 2;

export const slugifyStateId = (label: string, index: number): string => {
  const base = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
  return base && /^[a-z]/.test(base) ? base : `step_${index + 1}`;
};

/** Graph + structural validation; mirrors the server's rules exactly. */
export const validateWorkflow = (def: WorkflowV2): string[] => {
  const errors: string[] = [];
  if (!def.initial) errors.push("initial state is required");
  if (!Array.isArray(def.states) || def.states.length < 2) errors.push("at least 2 states are required");
  if (!Array.isArray(def.transitions) || def.transitions.length < 1) errors.push("at least 1 transition is required");
  if (errors.length > 0) return errors;

  const stateIds = new Set<string>();
  for (const s of def.states) {
    if (!s.id) errors.push(`state "${s.label || "(unnamed)"}" is missing an id`);
    else if (!/^[a-zA-Z][a-zA-Z0-9_-]*$/.test(s.id)) errors.push(`state id "${s.id}" must be alphanumeric/dash/underscore`);
    if (!s.label) errors.push(`state "${s.id}" is missing a label`);
    if (stateIds.has(s.id)) errors.push(`duplicate state id "${s.id}"`);
    stateIds.add(s.id);
  }
  if (!stateIds.has(def.initial)) errors.push(`initial state "${def.initial}" does not exist`);

  const sessionStates = def.states.filter((s) => s.ui === "pathway-session");
  if (sessionStates.length > 1) {
    errors.push(`only one state may host the pathway session (found ${sessionStates.length})`);
  }

  def.transitions.forEach((t, i) => {
    if (!t.label) errors.push(`transition ${i + 1} is missing a label`);
    if (!stateIds.has(t.from)) errors.push(`transition ${i + 1} ("${t.label}"): unknown from-state "${t.from}"`);
    if (!stateIds.has(t.to)) errors.push(`transition ${i + 1} ("${t.label}"): unknown to-state "${t.to}"`);
    if (t.guard?.type === "field") {
      const name = t.guard.field;
      const exists = def.states.some((st) => (st.capture ?? []).some((c) => c.id === name));
      if (!name) errors.push(`transition "${t.label}": field guard needs a field`);
      else if (!exists) errors.push(`transition "${t.label}" guards on field "${name}" which is not captured by any state`);
    }
  });

  const outgoing = new Map<string, WorkflowTransition[]>();
  for (const t of def.transitions) {
    if (!outgoing.has(t.from)) outgoing.set(t.from, []);
    outgoing.get(t.from)!.push(t);
  }
  for (const s of def.states) {
    const out = outgoing.get(s.id) ?? [];
    if (s.kind === "terminal" && out.length > 0) errors.push(`terminal state "${s.id}" must not have outgoing transitions`);
    if (s.kind !== "terminal" && out.length === 0) errors.push(`state "${s.id}" has no outgoing transitions (only terminal states may be dead ends)`);
  }

  if (stateIds.has(def.initial)) {
    const reachable = new Set<string>([def.initial]);
    const queue = [def.initial];
    while (queue.length > 0) {
      const cur = queue.shift()!;
      for (const t of outgoing.get(cur) ?? []) {
        if (stateIds.has(t.to) && !reachable.has(t.to)) { reachable.add(t.to); queue.push(t.to); }
      }
    }
    for (const s of def.states) {
      if (!reachable.has(s.id)) errors.push(`state "${s.id}" is unreachable from the initial state`);
    }
    if (!def.states.some((s) => s.kind === "terminal" && reachable.has(s.id))) {
      errors.push("no terminal state is reachable from the initial state");
    }
  }

  return errors;
};

/** Upgrade a legacy comma-separated chain into a linear v2 machine. */
export const upgradeLegacyWorkflow = (steps: string[]): WorkflowV2 => {
  const labels = steps.filter((s) => s && s.trim().length > 0);
  const used = new Set<string>();
  const states: WorkflowState[] = labels.map((label, i) => {
    let id = slugifyStateId(label, i);
    while (used.has(id)) id = `${id}_${i}`;
    used.add(id);
    return {
      id,
      label: label.trim(),
      kind: i === 0 ? "start" : i === labels.length - 1 ? "terminal" : "task",
    };
  });
  return {
    version: 2,
    initial: states[0]?.id ?? "start",
    states,
    transitions: states.slice(0, -1).map((s, i) => ({
      id: `t_${s.id}`,
      from: s.id,
      to: states[i + 1]!.id,
      label: "Complete",
      kind: "advance",
    })),
  };
};

/** Downgrade a v2 machine back to a plain label chain (loses everything else). */
export const downgradeToLegacy = (def: WorkflowV2): string[] => def.states.map((s) => s.label);
